import { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/pages/admin/components/AdminLayout';
import { supabase } from '@/lib/supabase';
import AddMemberModal from './AddMemberModal';
import SubordinatesDrawer from './SubordinatesDrawer';
import SetReferrerModal from './SetReferrerModal';
import ImageUploader from '@/components/base/ImageUploader';

interface Member {
  id: string;
  username: string;
  nickname: string | null;
  phone: string | null;
  avatar_url: string | null;
  level: string;
  points: number;
  balance: number;
  status: string;
  created_at: string;
}

interface PointsLog {
  id: string;
  delta: number;
  type: string;
  source: string;
  remark: string | null;
  balance_after: number;
  created_at: string;
}

interface BalanceLog {
  id: string;
  delta: number;
  type: string;
  source: string;
  remark: string | null;
  balance_after: number;
  related_order_id: string | null;
  created_at: string;
}

const levelColor: Record<string, string> = {
  '会员': 'bg-gray-100 text-gray-500',
  '销售员': 'bg-emerald-50 text-emerald-600',
  '批发': 'bg-sky-50 text-sky-600',
  '主管': 'bg-amber-50 text-amber-600',
  '经理': 'bg-orange-50 text-orange-600',
  '总监': 'bg-rose-50 text-rose-600',
  '董事': 'bg-violet-50 text-violet-600',
  '总裁': 'bg-red-50 text-red-600',
};

const statusStyle: Record<string, string> = {
  active: 'bg-emerald-50 text-emerald-600',
  disabled: 'bg-rose-50 text-rose-500',
  frozen: 'bg-sky-50 text-sky-500',
};

const statusLabel: Record<string, string> = {
  active: '正常',
  disabled: '禁用',
  frozen: '已冻结',
};

const sourceLabel: Record<string, string> = {
  manual: '后台调整',
  order: '订单消费',
  recharge: '充值获得',
  transfer_in: '积分转入',
  transfer_out: '积分转出',
  transfer: '积分转账',
  reward: '返佣收入',
  refund: '退款返还',
  refund_deduct: '退款扣回返佣',
  performance: '消费业绩',
};

const sourceIcon: Record<string, string> = {
  manual: 'ri-settings-3-line',
  order: 'ri-shopping-bag-line',
  recharge: 'ri-add-circle-line',
  transfer_in: 'ri-arrow-down-circle-line',
  transfer_out: 'ri-arrow-up-circle-line',
  transfer: 'ri-exchange-line',
  reward: 'ri-gift-line',
  refund: 'ri-refund-2-line',
  refund_deduct: 'ri-arrow-go-back-line',
  performance: 'ri-bar-chart-line',
};

const balanceSourceLabel: Record<string, string> = {
  manual: '后台调整',
  commission: '返佣收入',
  recharge: '充值',
  withdraw: '提现',
  refund: '退款返还',
  order: '订单消费',
  reward: '活动奖励',
};

const balanceSourceIcon: Record<string, string> = {
  manual: 'ri-settings-3-line',
  commission: 'ri-gift-line',
  recharge: 'ri-add-circle-line',
  withdraw: 'ri-arrow-up-circle-line',
  refund: 'ri-refund-2-line',
  order: 'ri-shopping-bag-line',
  reward: 'ri-trophy-line',
};

const QUICK_POINTS = [100, 500, 1000, 2000, 5000];
const QUICK_BALANCE = [10, 50, 100, 500, 1000];
const levels = ['全部', '会员', '销售员', '批发', '主管', '经理', '总监', '董事', '总裁'];

const AdminMembersPage = () => {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterLevel, setFilterLevel] = useState('全部');

  // 冻结/解冻会员
  const [freezeModal, setFreezeModal] = useState<{ member: Member; action: 'freeze' | 'unfreeze' } | null>(null);
  const [freezeSubmitting, setFreezeSubmitting] = useState(false);

  const handleFreezeToggle = async () => {
    if (!freezeModal) return;
    setFreezeSubmitting(true);
    const { member, action } = freezeModal;
    const newStatus = action === 'freeze' ? 'frozen' : 'active';
    const { error } = await supabase
      .from('custom_users')
      .update({ status: newStatus })
      .eq('id', member.id);
    if (!error) {
      setMembers((prev) => prev.map((m) => m.id === member.id ? { ...m, status: newStatus } : m));
      await supabase.from('admin_logs').insert({
        action: action === 'freeze' ? '冻结会员' : '解冻会员',
        target_type: '会员',
        target_id: member.id,
        detail: { memberName: member.nickname ?? member.username, newStatus },
      });
      setFreezeModal(null);
    }
    setFreezeSubmitting(false);
  };

  // 删除会员
  const [deleteModal, setDeleteModal] = useState<Member | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const handleDeleteMember = async () => {
    if (!deleteModal) return;
    setDeleteSubmitting(true);
    try {
      const adminToken = localStorage.getItem('admin_token') ?? '';
      const res = await fetch('https://pdxhoigcrxsqwxgrphdv.supabase.co/functions/v1/custom-auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'admin_delete_user', token: adminToken, userId: deleteModal.id }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        // 删除失败，不做任何 UI 变更
        setDeleteSubmitting(false);
        return;
      }
      await supabase.from('admin_logs').insert({
        action: '删除会员',
        target_type: '会员',
        target_id: deleteModal.id,
        detail: {
          memberName: deleteModal.nickname ?? deleteModal.username,
          phone: deleteModal.phone,
          level: deleteModal.level,
        },
      });
      setMembers((prev) => prev.filter((m) => m.id !== deleteModal.id));
      setDeleteModal(null);
    } catch {
      // 网络错误，不做 UI 变更
    }
    setDeleteSubmitting(false);
  };

  // 积分调整弹窗
  const [pointsModal, setPointsModal] = useState<Member | null>(null);
  const [pointsDelta, setPointsDelta] = useState('');
  const [pointsRemark, setPointsRemark] = useState('');
  const [pointsType, setPointsType] = useState<'add' | 'sub'>('add');
  const [pointsSubmitting, setPointsSubmitting] = useState(false);
  const [pointsError, setPointsError] = useState('');
  const [pointsSuccess, setPointsSuccess] = useState(false);
  const [pointsResultData, setPointsResultData] = useState<{ delta: number; newPoints: number; type: 'add' | 'sub' } | null>(null);

  // 余额调整弹窗
  const [balanceModal, setBalanceModal] = useState<Member | null>(null);
  const [balanceDelta, setBalanceDelta] = useState('');
  const [balanceRemark, setBalanceRemark] = useState('');
  const [balanceType, setBalanceType] = useState<'add' | 'sub'>('add');
  const [balanceSubmitting, setBalanceSubmitting] = useState(false);
  const [balanceError, setBalanceError] = useState('');
  const [balanceSuccess, setBalanceSuccess] = useState(false);
  const [balanceResultData, setBalanceResultData] = useState<{ delta: number; newBalance: number; type: 'add' | 'sub' } | null>(null);

  // 重置密码弹窗
  const [resetPwdModal, setResetPwdModal] = useState<Member | null>(null);
  const [resetPwdValue, setResetPwdValue] = useState('');
  const [resetPwdConfirm, setResetPwdConfirm] = useState('');
  const [resetPwdShow, setResetPwdShow] = useState(false);
  const [resetPwdShowConfirm, setResetPwdShowConfirm] = useState(false);
  const [resetPwdSubmitting, setResetPwdSubmitting] = useState(false);
  const [resetPwdError, setResetPwdError] = useState('');
  const [resetPwdSuccess, setResetPwdSuccess] = useState(false);

  // 导出Excel
  const exportToCSV = () => {
    const headers = ['昵称', '用户名', '手机号', '等级', '积分', '余额', '状态', '注册时间'];
    const rows = filtered.map(m => [
      m.nickname ?? '',
      m.username ?? '',
      m.phone ?? '',
      m.level,
      m.points,
      Number(m.balance).toFixed(2),
      m.status === 'active' ? '正常' : '禁用',
      new Date(m.created_at).toLocaleDateString('zh-CN'),
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `会员数据_${new Date().toLocaleDateString('zh-CN')}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  // 头像上传弹窗
  const [avatarModal, setAvatarModal] = useState<Member | null>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarSuccess, setAvatarSuccess] = useState(false);

  const handleAvatarUpload = async (url: string) => {
    if (!avatarModal) return;
    setAvatarUploading(true);
    const { error } = await supabase
      .from('custom_users')
      .update({ avatar_url: url })
      .eq('id', avatarModal.id);
    setAvatarUploading(false);
    if (!error) {
      setMembers((prev) => prev.map((m) => m.id === avatarModal.id ? { ...m, avatar_url: url } : m));
      setAvatarSuccess(true);
      setTimeout(() => {
        setAvatarModal(null);
        setAvatarSuccess(false);
      }, 1200);
    }
  };

  // 新增会员
  const [showAddModal, setShowAddModal] = useState(false);
  // 下级信息
  const [subsDrawer, setSubsDrawer] = useState<Member | null>(null);
  // 设置上级
  const [referrerModal, setReferrerModal] = useState<Member | null>(null);

  // 积分流水侧边抽屉
  const [logsDrawer, setLogsDrawer] = useState<Member | null>(null);
  const [logs, setLogs] = useState<PointsLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsTab, setLogsTab] = useState<'all' | 'income' | 'expense'>('all');
  const [logsMainTab, setLogsMainTab] = useState<'points' | 'balance'>('points');
  const [balanceLogs, setBalanceLogs] = useState<BalanceLog[]>([]);
  const [balanceLogsLoading, setBalanceLogsLoading] = useState(false);
  const [balanceLogsTab, setBalanceLogsTab] = useState<'all' | 'income' | 'expense'>('all');

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('custom_users')
      .select('id, username, nickname, phone, avatar_url, level, points, balance, status, created_at')
      .order('created_at', { ascending: false });
    if (!error && data) setMembers(data as Member[]);
    setLoading(false);
  }, []);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  // ── 积分弹窗 ──
  const openPointsModal = (m: Member) => {
    setPointsModal(m);
    setPointsDelta('');
    setPointsRemark('');
    setPointsType('add');
    setPointsError('');
    setPointsSuccess(false);
    setPointsResultData(null);
  };

  const handlePointsSubmit = async () => {
    if (!pointsModal) return;
    const delta = parseInt(pointsDelta, 10);
    if (!pointsDelta || isNaN(delta) || delta <= 0) {
      setPointsError('请输入有效的积分数量（正整数）');
      return;
    }
    setPointsSubmitting(true);
    setPointsError('');

    const newPoints = pointsType === 'add'
      ? pointsModal.points + delta
      : Math.max(0, pointsModal.points - delta);

    const { error: updateError } = await supabase
      .from('custom_users')
      .update({ points: newPoints })
      .eq('id', pointsModal.id);

    if (updateError) {
      setPointsError('操作失败，请重试');
      setPointsSubmitting(false);
      return;
    }

    await supabase.from('points_log').insert({
      user_id: pointsModal.id,
      delta: pointsType === 'add' ? delta : -delta,
      type: pointsType === 'add' ? 'income' : 'expense',
      source: 'manual',
      remark: pointsRemark.trim() || (pointsType === 'add' ? '后台手动增加' : '后台手动扣减'),
      balance_after: newPoints,
    });

    setMembers((prev) =>
      prev.map((m) => m.id === pointsModal.id ? { ...m, points: newPoints } : m)
    );

    // 写入管理日志
    await supabase.from('admin_logs').insert({
      action: '调整积分',
      target_type: '会员',
      target_id: pointsModal.id,
      detail: {
        memberName: pointsModal.nickname ?? pointsModal.username,
        delta: pointsType === 'add' ? delta : -delta,
        newPoints,
        remark: pointsRemark.trim() || null,
      },
    });

    setPointsResultData({ delta, newPoints, type: pointsType });
    setPointsSubmitting(false);
    setPointsSuccess(true);
  };

  // ── 余额弹窗 ──
  const openBalanceModal = (m: Member) => {
    setBalanceModal(m);
    setBalanceDelta('');
    setBalanceRemark('');
    setBalanceType('add');
    setBalanceError('');
    setBalanceSuccess(false);
    setBalanceResultData(null);
  };

  const handleBalanceSubmit = async () => {
    if (!balanceModal) return;
    const raw = balanceDelta.trim();
    const delta = parseFloat(raw);
    if (!raw || isNaN(delta) || delta <= 0) {
      setBalanceError('请输入有效的金额（正数）');
      return;
    }
    if (!/^\d+(\.\d{1,2})?$/.test(raw)) {
      setBalanceError('金额最多保留两位小数');
      return;
    }
    setBalanceSubmitting(true);
    setBalanceError('');

    const currentBalance = Number(balanceModal.balance);
    const newBalance = balanceType === 'add'
      ? parseFloat((currentBalance + delta).toFixed(2))
      : parseFloat(Math.max(0, currentBalance - delta).toFixed(2));

    const { error: updateError } = await supabase
      .from('custom_users')
      .update({ balance: newBalance })
      .eq('id', balanceModal.id);

    if (updateError) {
      setBalanceError('操作失败，请重试');
      setBalanceSubmitting(false);
      return;
    }

    // 写入余额流水
    await supabase.from('balance_log').insert({
      user_id: balanceModal.id,
      delta: balanceType === 'add' ? delta : -delta,
      type: balanceType === 'add' ? 'income' : 'expense',
      source: 'manual',
      remark: balanceRemark.trim() || (balanceType === 'add' ? '后台手动充值' : '后台手动扣减'),
      balance_after: newBalance,
    });

    setMembers((prev) =>
      prev.map((m) => m.id === balanceModal.id ? { ...m, balance: newBalance } : m)
    );

    // 写入管理日志
    await supabase.from('admin_logs').insert({
      action: '调整余额',
      target_type: '会员',
      target_id: balanceModal.id,
      detail: {
        memberName: balanceModal.nickname ?? balanceModal.username,
        delta: balanceType === 'add' ? delta : -delta,
        newBalance,
        remark: balanceRemark.trim() || null,
      },
    });

    setBalanceResultData({ delta, newBalance, type: balanceType });
    setBalanceSubmitting(false);
    setBalanceSuccess(true);
  };

  // ── 重置密码 ──
  const openResetPwdModal = (m: Member) => {
    setResetPwdModal(m);
    setResetPwdValue('');
    setResetPwdConfirm('');
    setResetPwdShow(false);
    setResetPwdShowConfirm(false);
    setResetPwdError('');
    setResetPwdSuccess(false);
  };

  const handleResetPwdSubmit = async () => {
    if (!resetPwdModal) return;
    if (resetPwdValue.length < 6) { setResetPwdError('密码至少需要6位'); return; }
    if (resetPwdValue.length > 32) { setResetPwdError('密码不能超过32位'); return; }
    if (resetPwdValue !== resetPwdConfirm) { setResetPwdError('两次输入的密码不一致'); return; }
    setResetPwdSubmitting(true);
    setResetPwdError('');
    try {
      const adminToken = localStorage.getItem('admin_token') ?? '';
      const res = await fetch('https://pdxhoigcrxsqwxgrphdv.supabase.co/functions/v1/custom-auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'admin_reset_password', token: adminToken, userId: resetPwdModal.id, newPassword: resetPwdValue }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setResetPwdError(data.error ?? '操作失败，请重试');
      } else {
        // 写入管理日志
        await supabase.from('admin_logs').insert({
          action: '重置密码',
          target_type: '会员',
          target_id: resetPwdModal.id,
          detail: { memberName: resetPwdModal.nickname ?? resetPwdModal.username },
        });
        setResetPwdSuccess(true);
      }
    } catch {
      setResetPwdError('网络错误，请重试');
    }
    setResetPwdSubmitting(false);
  };

  // ── 积分流水 ──
  const openLogsDrawer = async (m: Member) => {
    setLogsDrawer(m);
    setLogsTab('all');
    setLogsMainTab('points');
    setBalanceLogsTab('all');
    setLogsLoading(true);
    setBalanceLogsLoading(true);
    const [pointsRes, balanceRes] = await Promise.all([
      supabase
        .from('points_log')
        .select('id, delta, type, source, remark, balance_after, created_at')
        .eq('user_id', m.id)
        .order('created_at', { ascending: false })
        .limit(100),
      supabase
        .from('balance_log')
        .select('id, delta, type, source, remark, balance_after, related_order_id, created_at')
        .eq('user_id', m.id)
        .order('created_at', { ascending: false })
        .limit(100),
    ]);
    setLogs((pointsRes.data as PointsLog[]) ?? []);
    setBalanceLogs((balanceRes.data as BalanceLog[]) ?? []);
    setLogsLoading(false);
    setBalanceLogsLoading(false);
  };

  const filtered = members.filter((m) => {
    const matchSearch =
      (m.nickname ?? '').includes(search) ||
      (m.username ?? '').includes(search) ||
      (m.phone ?? '').includes(search);
    const matchLevel = filterLevel === '全部' || m.level === filterLevel;
    return matchSearch && matchLevel;
  });

  const totalCount = members.length;
  const thisMonth = members.filter((m) => {
    const d = new Date(m.created_at);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;
  const activeCount = members.filter((m) => m.status === 'active').length;
  const totalPoints = members.reduce((s, m) => s + m.points, 0);
  const totalBalance = members.reduce((s, m) => s + Number(m.balance), 0);

  const formatDate = (ts: string) =>
    new Date(ts).toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' });

  const formatTime = (ts: string) => {
    const d = new Date(ts);
    return `${d.getMonth() + 1}/${d.getDate()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  const filteredLogs = logs.filter((l) => {
    if (logsTab === 'all') return true;
    return l.type === logsTab;
  });

  const filteredBalanceLogs = balanceLogs.filter((l) => {
    if (balanceLogsTab === 'all') return true;
    return l.type === balanceLogsTab;
  });

  const deltaVal = parseInt(pointsDelta, 10);
  const validDelta = !isNaN(deltaVal) && deltaVal > 0;
  const balanceDeltaNum = parseFloat(balanceDelta.trim());
  const validBalanceDelta = !isNaN(balanceDeltaNum) && balanceDeltaNum > 0 && /^\d+(\.\d{1,2})?$/.test(balanceDelta.trim());

  return (
    <AdminLayout title="会员管理">
      {/* Stats */}
      <div className="grid grid-cols-5 gap-4 mb-5">
        {[
          { label: '会员总数', value: totalCount, icon: 'ri-group-line', color: 'text-rose-500 bg-rose-50' },
          { label: '本月新增', value: thisMonth, icon: 'ri-user-add-line', color: 'text-emerald-500 bg-emerald-50' },
          { label: '正常会员', value: activeCount, icon: 'ri-user-star-line', color: 'text-amber-500 bg-amber-50' },
          { label: '总积分持有', value: totalPoints.toLocaleString('zh-CN', { minimumFractionDigits: 3, maximumFractionDigits: 3 }), icon: 'ri-copper-coin-line', color: 'text-sky-500 bg-sky-50' },
          { label: '总余额持有', value: `¥${totalBalance.toFixed(2)}`, icon: 'ri-wallet-3-line', color: 'text-violet-500 bg-violet-50' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl p-4 flex items-center gap-3">
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${s.color}`}>
              <i className={`${s.icon} text-lg`} />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-gray-400 mb-0.5">{s.label}</p>
              <p className="text-lg font-bold text-gray-800 truncate">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
          <i className="ri-search-line text-gray-400 text-sm" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜索昵称/用户名/手机号"
            className="text-sm outline-none w-52 placeholder-gray-400"
          />
          {search && (
            <button onClick={() => setSearch('')} className="text-gray-300 hover:text-gray-500 cursor-pointer">
              <i className="ri-close-line text-sm" />
            </button>
          )}
        </div>
        <button
          onClick={fetchMembers}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm cursor-pointer whitespace-nowrap bg-white border border-gray-200 text-gray-600 hover:border-rose-300 transition-all"
        >
          <i className="ri-refresh-line text-sm" />刷新
        </button>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm cursor-pointer whitespace-nowrap bg-emerald-500 text-white hover:bg-emerald-600 transition-all"
        >
          <i className="ri-user-add-line text-sm" />新增会员
        </button>
        <button
          onClick={exportToCSV}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm cursor-pointer whitespace-nowrap bg-white border border-gray-200 text-gray-600 hover:border-rose-300 transition-all"
        >
          <i className="ri-download-line text-sm" />导出Excel
        </button>
        <div className="flex items-center gap-1 bg-white rounded-lg p-1 border border-gray-200 flex-wrap">
          {levels.map((l) => (
            <button
              key={l}
              onClick={() => setFilterLevel(l)}
              className={`px-3 py-1.5 rounded-md text-xs cursor-pointer whitespace-nowrap transition-all ${
                filterLevel === l ? 'bg-rose-500 text-white font-medium' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left text-xs text-gray-400 font-medium px-5 py-3.5">会员</th>
                <th className="text-left text-xs text-gray-400 font-medium px-5 py-3.5">等级</th>
                <th className="text-left text-xs text-gray-400 font-medium px-5 py-3.5">积分</th>
                <th className="text-left text-xs text-gray-400 font-medium px-5 py-3.5">余额</th>
                <th className="text-left text-xs text-gray-400 font-medium px-5 py-3.5">状态</th>
                <th className="text-left text-xs text-gray-400 font-medium px-5 py-3.5">注册时间</th>
                <th className="text-left text-xs text-gray-400 font-medium px-5 py-3.5">操作</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m, i) => (
                <tr key={m.id} className={`${i < filtered.length - 1 ? 'border-b border-gray-50' : ''} hover:bg-gray-50/50 transition-colors`}>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rose-400 to-orange-300 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {m.avatar_url ? (
                          <img src={m.avatar_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-white text-xs font-bold">
                            {(m.nickname ?? m.username ?? '?')[0].toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-gray-800 font-medium">{m.nickname ?? m.username}</p>
                        <p className="text-xs text-gray-400">{m.phone ?? `@${m.username}`}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${levelColor[m.level] ?? 'bg-gray-100 text-gray-500'}`}>
                      {m.level}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span className="text-sm text-rose-500 font-semibold">{m.points.toLocaleString('zh-CN', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</span>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-800 font-medium">¥{Number(m.balance).toFixed(2)}</td>
                  <td className="px-5 py-4">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusStyle[m.status] ?? 'bg-gray-100 text-gray-500'}`}>
                      {statusLabel[m.status] ?? m.status}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-400">{formatDate(m.created_at)}</td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => { setAvatarModal(m); setAvatarSuccess(false); }}
                        className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 whitespace-nowrap"
                      >
                        <i className="ri-image-edit-line mr-0.5" />改头像
                      </button>
                      <button
                        onClick={() => openPointsModal(m)}
                        className="text-xs bg-rose-500 text-white px-2.5 py-1 rounded-full cursor-pointer hover:bg-rose-600 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-copper-coin-line mr-1" />调整积分
                      </button>
                      <button
                        onClick={() => openBalanceModal(m)}
                        className="text-xs bg-emerald-500 text-white px-2.5 py-1 rounded-full cursor-pointer hover:bg-emerald-600 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-wallet-3-line mr-1" />调整余额
                      </button>
                      <button
                        onClick={() => openResetPwdModal(m)}
                        className="text-xs bg-amber-500 text-white px-2.5 py-1 rounded-full cursor-pointer hover:bg-amber-600 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-lock-password-line mr-1" />重置密码
                      </button>
                      <button
                        onClick={() => setSubsDrawer(m)}
                        className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 whitespace-nowrap"
                      >
                        <i className="ri-team-line mr-0.5" />下级
                      </button>
                      <button
                        onClick={() => setReferrerModal(m)}
                        className="text-xs text-sky-500 cursor-pointer hover:text-sky-700 whitespace-nowrap"
                      >
                        <i className="ri-links-line mr-0.5" />上级
                      </button>
                      <button
                        onClick={() => openLogsDrawer(m)}
                        className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 whitespace-nowrap"
                      >
                        <i className="ri-history-line mr-0.5" />流水
                      </button>
                      {m.status === 'frozen' ? (
                        <button
                          onClick={() => setFreezeModal({ member: m, action: 'unfreeze' })}
                          className="text-xs text-sky-500 cursor-pointer hover:text-sky-700 whitespace-nowrap"
                        >
                          <i className="ri-lock-unlock-line mr-0.5" />解冻
                        </button>
                      ) : (
                        <button
                          onClick={() => setFreezeModal({ member: m, action: 'freeze' })}
                          className="text-xs text-amber-500 cursor-pointer hover:text-amber-700 whitespace-nowrap"
                        >
                          <i className="ri-lock-2-line mr-0.5" />冻结
                        </button>
                      )}
                      <button
                        onClick={() => setDeleteModal(m)}
                        className="text-xs text-red-400 cursor-pointer hover:text-red-600 whitespace-nowrap"
                      >
                        <i className="ri-delete-bin-line mr-0.5" />删除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && filtered.length === 0 && (
          <div className="py-16 text-center text-gray-400 text-sm">暂无会员数据</div>
        )}
      </div>

      {!loading && filtered.length > 0 && (
        <div className="mt-4 text-xs text-gray-400 text-right">共 {filtered.length} 位会员</div>
      )}

      {/* ── 积分调整弹窗 ── */}
      {pointsModal && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => !pointsSubmitting && setPointsModal(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-sm p-6" style={{ animation: 'popIn 0.25s cubic-bezier(0.34,1.56,0.64,1)' }} onClick={(e) => e.stopPropagation()}>
              {pointsSuccess && pointsResultData ? (
                <div className="flex flex-col items-center py-2">
                  <div className={`w-20 h-20 flex items-center justify-center rounded-full mb-4 ${pointsResultData.type === 'add' ? 'bg-emerald-50' : 'bg-rose-50'}`} style={{ animation: 'popIn 0.4s cubic-bezier(0.34,1.56,0.64,1)' }}>
                    <i className={`text-4xl ${pointsResultData.type === 'add' ? 'ri-add-circle-line text-emerald-500' : 'ri-subtract-line text-rose-500'}`} />
                  </div>
                  <h3 className="font-bold text-gray-900 text-lg mb-1">{pointsResultData.type === 'add' ? '积分增加成功！' : '积分扣减成功！'}</h3>
                  <p className="text-sm text-gray-500 mb-4">{pointsResultData.type === 'add' ? '+' : '-'}{pointsResultData.delta.toLocaleString('zh-CN', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} 积分</p>
                  <div className="bg-gray-50 rounded-xl px-4 py-3 w-full mb-5 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">会员</span>
                      <span className="text-gray-700 font-medium">{pointsModal.nickname ?? pointsModal.username}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">调整后积分</span>
                      <span className="text-rose-500 font-bold">{pointsResultData.newPoints.toLocaleString('zh-CN', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</span>
                    </div>
                    {pointsRemark.trim() && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">备注</span>
                        <span className="text-gray-600">{pointsRemark.trim()}</span>
                      </div>
                    )}
                  </div>
                  <button onClick={() => setPointsModal(null)} className="w-full bg-rose-500 text-white py-3 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap">完成</button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-full bg-rose-50 flex items-center justify-center flex-shrink-0">
                      <i className="ri-copper-coin-line text-rose-500 text-xl" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-bold text-gray-900">调整积分</h3>
                      <p className="text-xs text-gray-400 truncate">{pointsModal.nickname ?? pointsModal.username}&nbsp;· 当前 <span className="text-rose-500 font-semibold">{pointsModal.points.toLocaleString('zh-CN', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</span> 积分</p>
                    </div>
                  </div>
                  <div className="flex gap-2 mb-4 p-1 bg-gray-100 rounded-xl">
                    {(['add', 'sub'] as const).map((t) => (
                      <button key={t} onClick={() => { setPointsType(t); setPointsError(''); }} className={`flex-1 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-all ${pointsType === t ? (t === 'add' ? 'bg-white text-emerald-600 shadow-sm' : 'bg-white text-rose-600 shadow-sm') : 'text-gray-400'}`}>
                        <i className={`${t === 'add' ? 'ri-add-line' : 'ri-subtract-line'} mr-1`} />{t === 'add' ? '增加积分' : '扣减积分'}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2 mb-3 flex-wrap">
                    {QUICK_POINTS.map((amt) => (
                      <button key={amt} onClick={() => { setPointsDelta(String(amt)); setPointsError(''); }} className={`px-3 py-1.5 rounded-lg text-xs cursor-pointer whitespace-nowrap border transition-all ${pointsDelta === String(amt) ? 'border-rose-400 bg-rose-50 text-rose-600 font-medium' : 'border-gray-200 text-gray-500 hover:border-rose-300'}`}>
                        {amt >= 1000 ? `${amt / 1000}k` : amt}
                      </button>
                    ))}
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">积分数量</label>
                    <div className="relative">
                      <input type="number" value={pointsDelta} onChange={(e) => { setPointsDelta(e.target.value); setPointsError(''); }} placeholder="输入积分数量" min="1" className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-rose-400 transition-colors pr-12" />
                      <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">积分</span>
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">备注（选填）</label>
                    <input type="text" value={pointsRemark} onChange={(e) => setPointsRemark(e.target.value)} placeholder="如：活动奖励、手动补偿..." maxLength={50} className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-rose-400 transition-colors" />
                  </div>
                  {validDelta && (
                    <div className="mb-4 bg-gray-50 rounded-xl px-4 py-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs text-gray-400">当前积分</span>
                        <span className="text-sm text-gray-600">{pointsModal.points.toLocaleString('zh-CN', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</span>
                      </div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs text-gray-400">{pointsType === 'add' ? '增加' : '扣减'}</span>
                        <span className={`text-sm font-medium ${pointsType === 'add' ? 'text-emerald-600' : 'text-rose-500'}`}>{pointsType === 'add' ? '+' : '-'}{deltaVal.toLocaleString('zh-CN', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</span>
                      </div>
                      <div className="border-t border-gray-200 pt-1.5 flex items-center justify-between">
                        <span className="text-xs text-gray-500 font-medium">调整后</span>
                        <span className="text-base font-bold text-gray-900">{pointsType === 'add' ? (pointsModal.points + deltaVal).toLocaleString('zh-CN', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : Math.max(0, pointsModal.points - deltaVal).toLocaleString('zh-CN', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</span>
                      </div>
                    </div>
                  )}
                  {pointsError && <div className="mb-4 flex items-center gap-2 bg-rose-50 text-rose-500 text-sm px-3 py-2.5 rounded-xl"><i className="ri-error-warning-line flex-shrink-0" />{pointsError}</div>}
                  <div className="flex gap-3">
                    <button onClick={() => setPointsModal(null)} disabled={pointsSubmitting} className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors disabled:opacity-50">取消</button>
                    <button onClick={handlePointsSubmit} disabled={pointsSubmitting || !validDelta} className={`flex-1 text-white py-2.5 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50 ${pointsType === 'add' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-rose-500 hover:bg-rose-600'}`}>
                      {pointsSubmitting ? <span className="flex items-center justify-center gap-2"><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />提交中...</span> : <span><i className={`${pointsType === 'add' ? 'ri-add-line' : 'ri-subtract-line'} mr-1`} />确认{pointsType === 'add' ? '增加' : '扣减'}</span>}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── 余额调整弹窗 ── */}
      {balanceModal && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => !balanceSubmitting && setBalanceModal(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-sm p-6" style={{ animation: 'popIn 0.25s cubic-bezier(0.34,1.56,0.64,1)' }} onClick={(e) => e.stopPropagation()}>
              {balanceSuccess && balanceResultData ? (
                <div className="flex flex-col items-center py-2">
                  <div className={`w-20 h-20 flex items-center justify-center rounded-full mb-4 ${balanceResultData.type === 'add' ? 'bg-emerald-50' : 'bg-orange-50'}`} style={{ animation: 'popIn 0.4s cubic-bezier(0.34,1.56,0.64,1)' }}>
                    <i className={`text-4xl ${balanceResultData.type === 'add' ? 'ri-wallet-3-line text-emerald-500' : 'ri-wallet-3-line text-orange-500'}`} />
                  </div>
                  <h3 className="font-bold text-gray-900 text-lg mb-1">{balanceResultData.type === 'add' ? '余额充值成功！' : '余额扣减成功！'}</h3>
                  <p className="text-sm text-gray-500 mb-4">{balanceResultData.type === 'add' ? '+' : '-'}¥{balanceResultData.delta.toFixed(2)}</p>
                  <div className="bg-gray-50 rounded-xl px-4 py-3 w-full mb-5 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">会员</span>
                      <span className="text-gray-700 font-medium">{balanceModal.nickname ?? balanceModal.username}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">调整后余额</span>
                      <span className="text-emerald-600 font-bold">¥{balanceResultData.newBalance.toFixed(2)}</span>
                    </div>
                    {balanceRemark.trim() && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">备注</span>
                        <span className="text-gray-600">{balanceRemark.trim()}</span>
                      </div>
                    )}
                  </div>
                  <button onClick={() => setBalanceModal(null)} className="w-full bg-emerald-500 text-white py-3 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-emerald-600 transition-colors">完成</button>
                </div>
              ) : (
                <>
                  {/* Header */}
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                      <i className="ri-wallet-3-line text-emerald-500 text-xl" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-bold text-gray-900">调整余额</h3>
                      <p className="text-xs text-gray-400 truncate">
                        {balanceModal.nickname ?? balanceModal.username}
                        &nbsp;· 当前 <span className="text-emerald-600 font-semibold">¥{Number(balanceModal.balance).toFixed(2)}</span>
                      </p>
                    </div>
                  </div>

                  {/* 充值/扣减切换 */}
                  <div className="flex gap-2 mb-4 p-1 bg-gray-100 rounded-xl">
                    {(['add', 'sub'] as const).map((t) => (
                      <button
                        key={t}
                        onClick={() => { setBalanceType(t); setBalanceError(''); }}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-all ${
                          balanceType === t
                            ? t === 'add'
                              ? 'bg-white text-emerald-600 shadow-sm'
                              : 'bg-white text-orange-600 shadow-sm'
                            : 'text-gray-400'
                        }`}
                      >
                        <i className={`${t === 'add' ? 'ri-add-circle-line' : 'ri-indeterminate-circle-line'} mr-1`} />
                        {t === 'add' ? '充值余额' : '扣减余额'}
                      </button>
                    ))}
                  </div>

                  {/* 快捷金额 */}
                  <div className="flex gap-2 mb-3 flex-wrap">
                    {QUICK_BALANCE.map((amt) => (
                      <button
                        key={amt}
                        onClick={() => { setBalanceDelta(String(amt)); setBalanceError(''); }}
                        className={`px-3 py-1.5 rounded-lg text-xs cursor-pointer whitespace-nowrap border transition-all ${
                          balanceDelta === String(amt)
                            ? 'border-emerald-400 bg-emerald-50 text-emerald-600 font-medium'
                            : 'border-gray-200 text-gray-500 hover:border-emerald-300'
                        }`}
                      >
                        ¥{amt >= 1000 ? `${amt / 1000}k` : amt}
                      </button>
                    ))}
                  </div>

                  {/* 金额输入 */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">金额</label>
                    <div className="relative">
                      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-gray-400 font-medium">¥</span>
                      <input
                        type="number"
                        value={balanceDelta}
                        onChange={(e) => { setBalanceDelta(e.target.value); setBalanceError(''); }}
                        placeholder="0.00"
                        min="0.01"
                        step="0.01"
                        className="w-full border border-gray-200 rounded-xl pl-8 pr-3.5 py-2.5 text-sm outline-none focus:border-emerald-400 transition-colors"
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">支持最多两位小数</p>
                  </div>

                  {/* 备注 */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">备注（选填）</label>
                    <input
                      type="text"
                      value={balanceRemark}
                      onChange={(e) => setBalanceRemark(e.target.value)}
                      placeholder="如：后台充值、活动奖励..."
                      maxLength={50}
                      className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-emerald-400 transition-colors"
                    />
                  </div>

                  {/* 预览 */}
                  {validBalanceDelta && (
                    <div className="mb-4 bg-gray-50 rounded-xl px-4 py-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs text-gray-400">当前余额</span>
                        <span className="text-sm text-gray-600">¥{Number(balanceModal.balance).toFixed(2)}</span>
                      </div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs text-gray-400">{balanceType === 'add' ? '充值' : '扣减'}</span>
                        <span className={`text-sm font-medium ${balanceType === 'add' ? 'text-emerald-600' : 'text-orange-500'}`}>
                          {balanceType === 'add' ? '+' : '-'}¥{balanceDeltaNum.toFixed(2)}
                        </span>
                      </div>
                      <div className="border-t border-gray-200 pt-1.5 flex items-center justify-between">
                        <span className="text-xs text-gray-500 font-medium">调整后</span>
                        <span className="text-base font-bold text-gray-900">
                          ¥{balanceType === 'add'
                            ? (Number(balanceModal.balance) + balanceDeltaNum).toFixed(2)
                            : Math.max(0, Number(balanceModal.balance) - balanceDeltaNum).toFixed(2)
                          }
                        </span>
                      </div>
                    </div>
                  )}

                  {balanceError && (
                    <div className="mb-4 flex items-center gap-2 bg-rose-50 text-rose-500 text-sm px-3 py-2.5 rounded-xl">
                      <i className="ri-error-warning-line flex-shrink-0" />{balanceError}
                    </div>
                  )}

                  {/* Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => setBalanceModal(null)}
                      disabled={balanceSubmitting}
                      className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      取消
                    </button>
                    <button
                      onClick={handleBalanceSubmit}
                      disabled={balanceSubmitting || !validBalanceDelta}
                      className={`flex-1 text-white py-2.5 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50 ${
                        balanceType === 'add' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-orange-500 hover:bg-orange-600'
                      }`}
                    >
                      {balanceSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          提交中...
                        </span>
                      ) : (
                        <span>
                          <i className={`${balanceType === 'add' ? 'ri-add-circle-line' : 'ri-indeterminate-circle-line'} mr-1`} />
                          确认{balanceType === 'add' ? '充值' : '扣减'}
                        </span>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── 重置密码弹窗 ── */}
      {resetPwdModal && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => !resetPwdSubmitting && setResetPwdModal(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-sm p-6" style={{ animation: 'popIn 0.25s cubic-bezier(0.34,1.56,0.64,1)' }} onClick={(e) => e.stopPropagation()}>
              {resetPwdSuccess ? (
                <div className="flex flex-col items-center py-2">
                  <div className="w-20 h-20 flex items-center justify-center rounded-full bg-emerald-50 mb-4" style={{ animation: 'popIn 0.4s cubic-bezier(0.34,1.56,0.64,1)' }}>
                    <i className="ri-shield-check-line text-4xl text-emerald-500" />
                  </div>
                  <h3 className="font-bold text-gray-900 text-lg mb-1">密码重置成功！</h3>
                  <p className="text-sm text-gray-500 mb-5 text-center"><span className="font-medium text-gray-700">{resetPwdModal.nickname ?? resetPwdModal.username}</span> 的登录密码已更新</p>
                  <div className="bg-amber-50 rounded-xl px-4 py-3 w-full mb-5">
                    <div className="flex items-start gap-2">
                      <i className="ri-information-line text-amber-500 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-amber-700">请将新密码告知用户，用户下次登录时使用新密码即可</p>
                    </div>
                  </div>
                  <button onClick={() => setResetPwdModal(null)} className="w-full bg-emerald-500 text-white py-3 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-emerald-600 transition-colors">完成</button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0">
                      <i className="ri-lock-password-line text-amber-500 text-xl" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-bold text-gray-900">重置登录密码</h3>
                      <p className="text-xs text-gray-400 truncate">{resetPwdModal.nickname ?? resetPwdModal.username}{resetPwdModal.phone && <span className="ml-1">· {resetPwdModal.phone}</span>}</p>
                    </div>
                  </div>
                  <div className="bg-amber-50 rounded-xl px-4 py-3 mb-4 flex items-start gap-2">
                    <i className="ri-information-line text-amber-500 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-700">管理员可直接为用户设置新密码，无需验证码。密码修改后立即生效，请妥善告知用户。</p>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">新密码</label>
                    <div className="relative">
                      <input type={resetPwdShow ? 'text' : 'password'} value={resetPwdValue} onChange={(e) => { setResetPwdValue(e.target.value); setResetPwdError(''); }} placeholder="请输入新密码（6-32位）" maxLength={32} className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors pr-10" />
                      <button type="button" onClick={() => setResetPwdShow(!resetPwdShow)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer w-5 h-5 flex items-center justify-center">
                        <i className={resetPwdShow ? 'ri-eye-off-line' : 'ri-eye-line'} />
                      </button>
                    </div>
                    {resetPwdValue && (
                      <div className="mt-1.5 flex items-center gap-1.5">
                        {[{ label: '长度', ok: resetPwdValue.length >= 6 }, { label: '字母', ok: /[a-zA-Z]/.test(resetPwdValue) }, { label: '数字', ok: /\d/.test(resetPwdValue) }].map((item) => (
                          <span key={item.label} className={`text-xs px-2 py-0.5 rounded-full ${item.ok ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-400'}`}>
                            {item.ok ? <i className="ri-check-line mr-0.5" /> : null}{item.label}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">确认新密码</label>
                    <div className="relative">
                      <input type={resetPwdShowConfirm ? 'text' : 'password'} value={resetPwdConfirm} onChange={(e) => { setResetPwdConfirm(e.target.value); setResetPwdError(''); }} placeholder="再次输入新密码" maxLength={32} className={`w-full border rounded-xl px-3.5 py-2.5 text-sm outline-none transition-colors pr-10 ${resetPwdConfirm && resetPwdValue !== resetPwdConfirm ? 'border-rose-300 focus:border-rose-400' : 'border-gray-200 focus:border-amber-400'}`} />
                      <button type="button" onClick={() => setResetPwdShowConfirm(!resetPwdShowConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer w-5 h-5 flex items-center justify-center">
                        <i className={resetPwdShowConfirm ? 'ri-eye-off-line' : 'ri-eye-line'} />
                      </button>
                    </div>
                    {resetPwdConfirm && resetPwdValue !== resetPwdConfirm && <p className="text-xs text-rose-500 mt-1">两次密码不一致</p>}
                    {resetPwdConfirm && resetPwdValue === resetPwdConfirm && resetPwdValue.length >= 6 && <p className="text-xs text-emerald-500 mt-1"><i className="ri-check-line" /> 密码一致</p>}
                  </div>
                  {resetPwdError && <div className="mb-4 flex items-center gap-2 bg-rose-50 text-rose-500 text-sm px-3 py-2.5 rounded-xl"><i className="ri-error-warning-line flex-shrink-0" />{resetPwdError}</div>}
                  <div className="flex gap-3">
                    <button onClick={() => setResetPwdModal(null)} disabled={resetPwdSubmitting} className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors disabled:opacity-50">取消</button>
                    <button onClick={handleResetPwdSubmit} disabled={resetPwdSubmitting || resetPwdValue.length < 6 || resetPwdValue !== resetPwdConfirm} className="flex-1 bg-amber-500 text-white py-2.5 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-amber-600 transition-colors disabled:opacity-50">
                      {resetPwdSubmitting ? <span className="flex items-center justify-center gap-2"><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />提交中...</span> : <span><i className="ri-shield-check-line mr-1" />确认重置</span>}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── 积分流水侧边抽屉 ── */}
      {logsDrawer && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => setLogsDrawer(null)} />
          <div className="fixed top-0 right-0 h-full w-[440px] bg-white z-50 flex flex-col" style={{ animation: 'slideInRight 0.25s ease' }} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 flex-shrink-0">
              <button onClick={() => setLogsDrawer(null)} className="w-8 h-8 flex items-center justify-center cursor-pointer text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
                <i className="ri-close-line text-xl" />
              </button>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-gray-900 text-base">流水明细</h3>
                <p className="text-xs text-gray-400 truncate">{logsDrawer.nickname ?? logsDrawer.username}</p>
              </div>
              <button onClick={() => { setLogsDrawer(null); openPointsModal(logsDrawer); }} className="flex items-center gap-1.5 bg-rose-500 text-white text-xs px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap hover:bg-rose-600 transition-colors">
                <i className="ri-copper-coin-line" />调整积分
              </button>
            </div>

            {/* 主 Tab：积分 / 余额返佣 */}
            <div className="flex border-b border-gray-100 flex-shrink-0 bg-gray-50/50">
              {([['points', 'ri-copper-coin-line', '积分流水'], ['balance', 'ri-wallet-3-line', '余额/返佣']] as const).map(([key, icon, label]) => (
                <button
                  key={key}
                  onClick={() => setLogsMainTab(key)}
                  className={`flex-1 py-3 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5 ${logsMainTab === key ? 'text-rose-500 border-b-2 border-rose-500 bg-white' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <i className={icon} />{label}
                </button>
              ))}
            </div>

            {/* ── 积分流水内容 ── */}
            {logsMainTab === 'points' && (
              <>
                <div className="flex border-b border-gray-100 flex-shrink-0">
                  {([['all', '全部'], ['income', '收入'], ['expense', '支出']] as ['all' | 'income' | 'expense', string][]).map(([key, label]) => (
                    <button key={key} onClick={() => setLogsTab(key)} className={`flex-1 py-2.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${logsTab === key ? 'text-rose-500 border-b-2 border-rose-500' : 'text-gray-400'}`}>
                      {label}{key !== 'all' && <span className="ml-1 text-xs">({logs.filter((l) => l.type === key).length})</span>}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-3 divide-x divide-gray-100 border-b border-gray-100 flex-shrink-0">
                  {[
                    { label: '累计获得', value: `+${logs.filter((l) => l.type === 'income').reduce((s, l) => s + l.delta, 0).toLocaleString('zh-CN', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`, color: 'text-emerald-500' },
                    { label: '累计消耗', value: `-${logs.filter((l) => l.type === 'expense').reduce((s, l) => s + Math.abs(l.delta), 0).toLocaleString('zh-CN', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`, color: 'text-rose-500' },
                    { label: '当前积分', value: logsDrawer.points.toLocaleString('zh-CN', { minimumFractionDigits: 3, maximumFractionDigits: 3 }), color: 'text-gray-700' },
                  ].map((s) => (
                    <div key={s.label} className="py-3 text-center">
                      <p className={`text-sm font-bold ${s.color}`}>{s.value}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{s.label}</p>
                    </div>
                  ))}
                </div>
                <div className="flex-1 overflow-y-auto">
                  {logsLoading ? (
                    <div className="flex items-center justify-center py-16"><div className="w-6 h-6 border-2 border-rose-400 border-t-transparent rounded-full animate-spin" /></div>
                  ) : filteredLogs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                      <i className="ri-file-list-3-line text-4xl mb-3" />
                      <p className="text-sm">暂无积分记录</p>
                    </div>
                  ) : (
                    <ul className="divide-y divide-gray-50">
                      {filteredLogs.map((log) => {
                        const isIncome = log.type === 'income';
                        const isRefundDeduct = log.source === 'refund_deduct';
                        const isReward = log.source === 'reward';
                        const icon = sourceIcon[log.source] ?? 'ri-copper-coin-line';
                        const label = sourceLabel[log.source] ?? log.source;
                        const iconBg = isRefundDeduct ? 'bg-orange-50' : isReward ? 'bg-amber-50' : isIncome ? 'bg-emerald-50' : 'bg-rose-50';
                        const iconColor = isRefundDeduct ? 'text-orange-500' : isReward ? 'text-amber-500' : isIncome ? 'text-emerald-500' : 'text-rose-400';
                        return (
                          <li key={log.id} className="flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50/50 transition-colors">
                            <div className={`w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0 ${iconBg}`}>
                              <i className={`${icon} text-sm ${iconColor}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <p className="text-sm text-gray-800 font-medium">{label}</p>
                                {isRefundDeduct && (
                                  <span className="text-[10px] bg-orange-50 text-orange-500 px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap">扣回</span>
                                )}
                                {isReward && (
                                  <span className="text-[10px] bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap">返佣</span>
                                )}
                              </div>
                              {log.remark && <p className="text-xs text-gray-400 truncate mt-0.5">{log.remark}</p>}
                              <p className="text-xs text-gray-300 mt-0.5">{formatTime(log.created_at)}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className={`text-sm font-bold ${isRefundDeduct ? 'text-orange-500' : isIncome ? 'text-emerald-500' : 'text-rose-500'}`}>{isIncome ? '+' : ''}{log.delta.toLocaleString('zh-CN', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</p>
                              <p className="text-xs text-gray-300 mt-0.5">余 {log.balance_after.toLocaleString('zh-CN', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</p>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </>
            )}

            {/* ── 余额/返佣内容 ── */}
            {logsMainTab === 'balance' && (
              <>
                <div className="flex border-b border-gray-100 flex-shrink-0">
                  {([['all', '全部'], ['income', '收入'], ['expense', '支出']] as ['all' | 'income' | 'expense', string][]).map(([key, label]) => (
                    <button key={key} onClick={() => setBalanceLogsTab(key)} className={`flex-1 py-2.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${balanceLogsTab === key ? 'text-emerald-500 border-b-2 border-emerald-500' : 'text-gray-400'}`}>
                      {label}{key !== 'all' && <span className="ml-1 text-xs">({balanceLogs.filter((l) => l.type === key).length})</span>}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-3 divide-x divide-gray-100 border-b border-gray-100 flex-shrink-0">
                  {[
                    { label: '累计收入', value: `+¥${balanceLogs.filter((l) => l.type === 'income').reduce((s, l) => s + Number(l.delta), 0).toFixed(2)}`, color: 'text-emerald-500' },
                    { label: '返佣收入', value: `+¥${balanceLogs.filter((l) => l.source === 'commission').reduce((s, l) => s + Number(l.delta), 0).toFixed(2)}`, color: 'text-amber-500' },
                    { label: '当前余额', value: `¥${Number(logsDrawer.balance).toFixed(2)}`, color: 'text-gray-700' },
                  ].map((s) => (
                    <div key={s.label} className="py-3 text-center">
                      <p className={`text-sm font-bold ${s.color}`}>{s.value}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{s.label}</p>
                    </div>
                  ))}
                </div>
                <div className="flex-1 overflow-y-auto">
                  {balanceLogsLoading ? (
                    <div className="flex items-center justify-center py-16"><div className="w-6 h-6 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" /></div>
                  ) : filteredBalanceLogs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                      <i className="ri-wallet-3-line text-4xl mb-3" />
                      <p className="text-sm">暂无余额记录</p>
                      <p className="text-xs mt-1 text-gray-300">返佣、充值、扣减等记录将显示在这里</p>
                    </div>
                  ) : (
                    <ul className="divide-y divide-gray-50">
                      {filteredBalanceLogs.map((log) => {
                        const isIncome = log.type === 'income';
                        const isCommission = log.source === 'commission';
                        const icon = balanceSourceIcon[log.source] ?? 'ri-wallet-3-line';
                        const label = balanceSourceLabel[log.source] ?? log.source;
                        return (
                          <li key={log.id} className="flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50/50 transition-colors">
                            <div className={`w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0 ${isCommission ? 'bg-amber-50' : isIncome ? 'bg-emerald-50' : 'bg-rose-50'}`}>
                              <i className={`${icon} text-sm ${isCommission ? 'text-amber-500' : isIncome ? 'text-emerald-500' : 'text-rose-400'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <p className="text-sm text-gray-800 font-medium">{label}</p>
                                {isCommission && (
                                  <span className="text-[10px] bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap">返佣</span>
                                )}
                              </div>
                              {log.remark && <p className="text-xs text-gray-400 truncate mt-0.5">{log.remark}</p>}
                              <p className="text-xs text-gray-300 mt-0.5">{formatTime(log.created_at)}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className={`text-sm font-bold ${isIncome ? 'text-emerald-500' : 'text-rose-500'}`}>
                                {isIncome ? '+' : '-'}¥{Math.abs(Number(log.delta)).toFixed(2)}
                              </p>
                              <p className="text-xs text-gray-300 mt-0.5">余 ¥{Number(log.balance_after).toFixed(2)}</p>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* ── 冻结/解冻确认弹窗 ── */}
      {freezeModal && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => !freezeSubmitting && setFreezeModal(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="bg-white rounded-2xl w-full max-w-sm p-6"
              style={{ animation: 'popIn 0.25s cubic-bezier(0.34,1.56,0.64,1)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-col items-center text-center mb-5">
                <div className={`w-16 h-16 flex items-center justify-center rounded-full mb-4 ${freezeModal.action === 'freeze' ? 'bg-sky-50' : 'bg-emerald-50'}`}>
                  <i className={`text-3xl ${freezeModal.action === 'freeze' ? 'ri-lock-2-line text-sky-500' : 'ri-lock-unlock-line text-emerald-500'}`} />
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-1.5">
                  {freezeModal.action === 'freeze' ? '确认冻结该会员？' : '确认解冻该会员？'}
                </h3>
                <p className="text-sm text-gray-500">
                  会员 <span className="font-semibold text-gray-700">「{freezeModal.member.nickname ?? freezeModal.member.username}」</span>
                </p>
              </div>
              <div className={`rounded-xl px-4 py-3 mb-5 flex items-start gap-2 ${freezeModal.action === 'freeze' ? 'bg-sky-50' : 'bg-emerald-50'}`}>
                <i className={`mt-0.5 flex-shrink-0 ${freezeModal.action === 'freeze' ? 'ri-information-line text-sky-500' : 'ri-information-line text-emerald-500'}`} />
                <p className={`text-xs ${freezeModal.action === 'freeze' ? 'text-sky-700' : 'text-emerald-700'}`}>
                  {freezeModal.action === 'freeze'
                    ? '冻结后该会员积分和余额不受影响，仍可正常登录和消费，但其下级的订单将不再为其产生返佣。'
                    : '解冻后该会员恢复正常状态，下级订单将重新为其产生返佣。'}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setFreezeModal(null)}
                  disabled={freezeSubmitting}
                  className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  取消
                </button>
                <button
                  onClick={handleFreezeToggle}
                  disabled={freezeSubmitting}
                  className={`flex-1 text-white py-2.5 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50 ${freezeModal.action === 'freeze' ? 'bg-sky-500 hover:bg-sky-600' : 'bg-emerald-500 hover:bg-emerald-600'}`}
                >
                  {freezeSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      处理中...
                    </span>
                  ) : (
                    <span>
                      <i className={`${freezeModal.action === 'freeze' ? 'ri-lock-2-line' : 'ri-lock-unlock-line'} mr-1`} />
                      确认{freezeModal.action === 'freeze' ? '冻结' : '解冻'}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── 删除会员确认弹窗 ── */}
      {deleteModal && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => !deleteSubmitting && setDeleteModal(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="bg-white rounded-2xl w-full max-w-sm p-6"
              style={{ animation: 'popIn 0.25s cubic-bezier(0.34,1.56,0.64,1)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-col items-center text-center mb-5">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-red-50 mb-4">
                  <i className="ri-delete-bin-2-line text-3xl text-red-500" />
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-1.5">确认删除会员？</h3>
                <p className="text-sm text-gray-500">
                  即将删除会员 <span className="font-semibold text-gray-700">「{deleteModal.nickname ?? deleteModal.username}」</span>
                </p>
                <p className="text-sm text-gray-500 mt-0.5">此操作不可撤销，账号数据将永久清除</p>
              </div>
              <div className="bg-red-50 rounded-xl px-4 py-3 mb-5 flex items-start gap-2">
                <i className="ri-error-warning-line text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-600">删除后该会员的积分、余额、订单记录等关联数据将无法恢复，请谨慎操作。</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteModal(null)}
                  disabled={deleteSubmitting}
                  className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  取消
                </button>
                <button
                  onClick={handleDeleteMember}
                  disabled={deleteSubmitting}
                  className="flex-1 bg-red-500 text-white py-2.5 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-red-600 transition-colors disabled:opacity-50"
                >
                  {deleteSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      删除中...
                    </span>
                  ) : (
                    <span><i className="ri-delete-bin-line mr-1" />确认删除</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── 头像上传弹窗 ── */}
      {avatarModal && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => !avatarUploading && setAvatarModal(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="bg-white rounded-2xl w-full max-w-xs p-6"
              style={{ animation: 'popIn 0.25s cubic-bezier(0.34,1.56,0.64,1)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-full bg-violet-50 flex items-center justify-center flex-shrink-0">
                  <i className="ri-image-edit-line text-violet-500 text-xl" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">修改头像</h3>
                  <p className="text-xs text-gray-400 truncate">{avatarModal.nickname ?? avatarModal.username}</p>
                </div>
              </div>

              {/* 当前头像预览 */}
              <div className="flex flex-col items-center mb-5">
                <div className="w-20 h-20 rounded-full overflow-hidden bg-gradient-to-br from-rose-400 to-orange-300 flex items-center justify-center mb-3 flex-shrink-0">
                  {avatarModal.avatar_url ? (
                    <img src={avatarModal.avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-white text-2xl font-bold">
                      {(avatarModal.nickname ?? avatarModal.username ?? '?')[0].toUpperCase()}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-400">{avatarModal.avatar_url ? '当前头像' : '暂无头像'}</p>
              </div>

              {avatarSuccess ? (
                <div className="flex flex-col items-center py-2">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-emerald-50 mb-3">
                    <i className="ri-check-line text-emerald-500 text-2xl" />
                  </div>
                  <p className="text-sm font-medium text-gray-700">头像更新成功！</p>
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <ImageUploader
                      value={avatarModal.avatar_url ?? ''}
                      onUpload={handleAvatarUpload}
                      variant="button"
                      placeholder="点击上传新头像"
                      folder="avatars"
                      disabled={avatarUploading}
                    />
                    <p className="text-xs text-gray-400 mt-2">支持 JPG、PNG、WebP，建议正方形图片，最大 10MB</p>
                  </div>
                  <button
                    onClick={() => setAvatarModal(null)}
                    disabled={avatarUploading}
                    className="w-full border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    关闭
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {showAddModal && (
        <AddMemberModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => { setShowAddModal(false); fetchMembers(); }}
        />
      )}

      {subsDrawer && (
        <SubordinatesDrawer
          member={subsDrawer}
          onClose={() => setSubsDrawer(null)}
        />
      )}

      {referrerModal && (
        <SetReferrerModal
          member={referrerModal}
          onClose={() => setReferrerModal(null)}
          onSuccess={() => { setReferrerModal(null); fetchMembers(); }}
        />
      )}

      <style>{`
        @keyframes popIn { from { transform: scale(0.85); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
      `}</style>
    </AdminLayout>
  );
};

export default AdminMembersPage;
